import { create } from "zustand";

interface CountryInfo {
  countryCode: string;
  countryName: string;
}

interface CountryBlockingActions {
  checkAccess: () => Promise<void>;
}

interface CountryBlockingState {
  isChecking: boolean;
  isBlocked: boolean;
  hasBeenChecked: boolean;

  actions: CountryBlockingActions;
}

const BLOCKED_COUNTRIES = [
  "CN", // China
  "RU", // Russia
  "BY", // Belarus
  "IR", // Iran
  "SY", // Syria
  "KP", // North Korea
  "MM", // Myanmar
  "VE", // Venezuela
  "CU", // Cuba
] as const;

// Country codes should be in ISO 3166-1 alpha-2 format (2-letter codes)
// You can find country codes at: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2

// Helper function to get the correct API URL based on environment
const getFreeIpApiUrl = () => {
  if (import.meta.env.DEV) {
    // In development, use the proxy
    return "/freeipapi/api/json/";
  } else {
    // In production, use the full URL
    return "https://free.freeipapi.com/api/json/";
  }
};

const useCountryBlockingStore = create<CountryBlockingState>((set, get) => {
  function isCountryBlocked(countryCode: string) {
    return BLOCKED_COUNTRIES.some(
      (country) => country === countryCode.toUpperCase()
    );
  }

  async function getUserCountry(): Promise<CountryInfo | null> {
    try {
      // Use environment-appropriate URL
      const apiUrl = getFreeIpApiUrl();
      const response = await fetch(apiUrl);

      if (!response.ok) {
        console.warn("Failed to fetch country info:", response.status);
        return null;
      }

      const data = await response.json();

      // Check if data is an array or object
      let countryData;
      if (Array.isArray(data)) {
        if (data.length === 0) {
          return null;
        }
        countryData = data[0];
      } else {
        // If it's a single object
        countryData = data;
      }

      if (
        !countryData ||
        !countryData.countryCode ||
        !countryData.countryName
      ) {
        console.warn("Invalid country data structure:", countryData);
        return null;
      }

      return {
        countryCode: countryData.countryCode,
        countryName: countryData.countryName,
      };
    } catch (error) {
      console.error("Error fetching country info:", error);
      return null;
    }
  }

  return {
    isChecking: true,
    isBlocked: false,
    hasBeenChecked: false,

    actions: {
      checkAccess: async () => {
        if (get().hasBeenChecked) return;

        try {
          const result = await getUserCountry();

          if (!result) {
            // Fallback: allow access if we can't determine country
            set({
              isBlocked: false,
              hasBeenChecked: true,
              isChecking: false,
            });
            return;
          }

          const isBlocked = isCountryBlocked(result.countryCode);

          set({
            isBlocked,
            hasBeenChecked: true,
            isChecking: false,
          });
        } catch (error) {
          console.error("Error checking country access:", error);
          set({
            isBlocked: false, // Fallback: allow access
            hasBeenChecked: true,
            isChecking: false,
          });
        }
      },
    },
  };
});

export const useIsCountryBlockingChecking = () =>
  useCountryBlockingStore((state) => state.isChecking);

export const useIsCountryBlockingBlocked = () =>
  useCountryBlockingStore((state) => state.isBlocked);

export const useCountryBlockingHasBeenChecked = () =>
  useCountryBlockingStore((state) => state.hasBeenChecked);

export const useCountryBlockingActions = () =>
  useCountryBlockingStore((state) => state.actions);
